import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getInitials } from "../../../utils/util";
import Skeleton from "react-loading-skeleton";
import axios from "axios";
import { useParams } from "react-router-dom";
import apiClient from "../../../apiClient/index";
import {
  DASHBOARD_SITE_STATS,
  FETCH_REGION_WISE_SCORE,
  FETCH_SUB_SITE_WITH_GROUPID,
  USER_WISE_INSPECTION_REPORT,
} from "../../../apiClient/endpoints";
import BarChart from "../../Charts/BarChart";
import GroupList from "../../GroupList";
import Filter from "./Filter";
import DateRangeFilterV2 from "../../dashboard/DateRangeFilterV2";
import NegativeResponsesV2 from "../../dashboard/NegativeResponsesV2";
import GroupWiseReportV2 from "../../dashboard/GroupWiseReportV2";
import SiteWiseInspectionStatsV2 from "./SiteWiseInspectionStatsV2";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

const getFilters = (options = []) => [
  {
    name: "Inspection Type",
    tags:
      options?.mainSites?.map((ele) => ({
        name: ele?.name,
        value: ele?.uuid,
        key: "inspectionType",
      })) || [],
  },
  {
    name: "Site",
    tags:
      options?.sites?.map((ele) => ({
        name: ele?.name,
        value: ele?.uuid,
        key: "site",
      })) || [],
  },
  {
    name: "OBR Org",
    tags:
      options?.obrOrg?.map((ele) => ({
        name: ele?.name,
        value: ele?.value,
        key: "obrOrg",
      })) || [],
  },
  {
    name: "OBR Bu",
    tags:
      options?.obrBu?.map((ele) => ({
        name: ele?.name,
        value: ele?.value,
        key: "obrBu",
      })) || [],
  },
];

const SiteDashboardV2 = () => {
  const userData = useSelector((state) => state.userSlice);
  const { groupId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [regionResponseStats, setRegionResponseStats] = useState([]);
  const [options, setOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [groupInfo, setGroupInfo] = useState(null);
  const [dates, setDates] = useState({ start: null, end: null });
  const [list, setList] = useState([]);
  const [siteStats, setSiteStats] = useState({});
  const [userWiseInspectionData, setUserWiseInspectionData] = useState([]);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [inspectionDataLoading, setInspectionDataLoading] = useState(true);
  const [resetStateFlag, setResetStateFlag] = useState(false);
  const filterOptions = getFilters(options);

  const filterPayload = () => ({
    site: selectedCheckboxes?.filter((e) => e.key === "site").map((e) => e.value) || [],
    obrBu: selectedCheckboxes?.filter((e) => e.key === "obrBu").map((e) => e.value) || [],
    obrOrg: selectedCheckboxes?.filter((e) => e.key === "obrOrg").map((e) => e.value) || [],
    mainSites: selectedCheckboxes?.filter((e) => e.key === "inspectionType").map((e) => e.value) || [],
  });

  const fetchGroupDetail = async () => {
    setGroupInfo(null);
    const res = await apiClient.get(`${configData.config.UNO_URL}/api/user/group/details/${groupId}`, {
      headers: { Authorization: `Bearer ${userData?.token}` },
    });
    res ? setGroupInfo(res) : setGroupInfo({});
  };

  const fetchNegativeResponses = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/${groupId}`,
        {
          machineIds: [],
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
          isSite: true,
          filters: filterPayload(),
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setQuestions(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchNegativeResponsesGroupwise = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/byGroup/${groupId}`,
        {
          machineIds: [],
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
          isSite: true,
          filters: filterPayload(),
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setList(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchSiteStats = async () => {
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${DASHBOARD_SITE_STATS}/${groupId}`, {
        dates: {
          startDate: dates.start ? dates.start.toISOString() : null,
          endDate: dates.start ? dates.end.toISOString() : null,
        },
        filters: filterPayload(),
      });
      setSiteStats(res);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchRegionWiseResponse = async () => {
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${FETCH_REGION_WISE_SCORE}/${groupId}`, {
        dates: {
          startDate: dates.start ? dates.start.toISOString() : null,
          endDate: dates.start ? dates.end.toISOString() : null,
        },
        filters: filterPayload(),
      });
      setRegionResponseStats(res);
    } catch (error) {
      console.log(error);
    }
  };

  const fetcSiteFilters = async () => {
    try {
      const res = await apiClient.get(`${configData.config.API_URL}${FETCH_SUB_SITE_WITH_GROUPID}/${groupId}`);
      setOptions(res);
    } catch (error) {
      console.log(error.message);
    }
  };

  const userWiseInspectionReport = async () => {
    try {
      setInspectionDataLoading(true);
      const res = await apiClient.post(`${configData.config.API_URL}${USER_WISE_INSPECTION_REPORT}/${groupId}`, {
        dates: {
          startDate: dates.start ? dates.start.toISOString() : null,
          endDate: dates.start ? dates.end.toISOString() : null,
        },
        filters: filterPayload(),
      });
      setUserWiseInspectionData(res);
    } catch (error) {
      console.log(error.message);
    } finally {
      setInspectionDataLoading(false);
    }
  };

  const Reload = async () => {
    setLoading(true);
    await Promise.all([
      fetchSiteStats(),
      fetchRegionWiseResponse(),
      fetchNegativeResponses(),
      fetchNegativeResponsesGroupwise(),
      userWiseInspectionReport(),
    ]);
    setLoading(false);
  };

  const RefreshPage = async () => {
    setLoading(true);
    setUserWiseInspectionData([]);
    await Promise.all([fetchGroupDetail(), fetcSiteFilters()]);
    setDates({ start: null, end: null });
    Reload();
  };

  useEffect(() => {
    if (!loading) Reload();
  }, [dates]);

  useEffect(() => {
    if (!groupId?.length) return;
    setActiveTab("dashboard");
    RefreshPage();
  }, [groupId]);

  useEffect(() => {
    if (resetStateFlag) Reload();
  }, [resetStateFlag]);

  return (
    <div className="dashboard-v2 site-dashboard-v2">
      <div className="dashboard-v2__top">
        <div className="machines-v2__tabs dashboard-v2__tabs site-dashboard-v2__tabs">
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            Site Dashboard
          </button>
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "groups" ? "active" : ""}`}
            onClick={() => setActiveTab("groups")}
          >
            Groups
          </button>
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "inspection" ? "active" : ""}`}
            onClick={() => setActiveTab("inspection")}
          >
            Site Wise Inspection Report
          </button>
        </div>

        {activeTab !== "groups" && (
          <div className="dashboard-v2__filters">
            <DateRangeFilterV2 dates={dates} setDates={setDates} />
            <Filter
              variant="v2"
              filterOptions={filterOptions}
              selectedCheckboxes={selectedCheckboxes}
              setSelectedCheckboxes={setSelectedCheckboxes}
              clickHandler={Reload}
              setResetStateFlag={setResetStateFlag}
            />
          </div>
        )}
      </div>

      {activeTab === "groups" ? (
        <div className="machines-v2__groups">
          <GroupList setActiveTab={() => setActiveTab("dashboard")} isSite={true} navigateLink="/s/dashboard" />
        </div>
      ) : activeTab === "inspection" ? (
        <SiteWiseInspectionStatsV2 loading={inspectionDataLoading} data={userWiseInspectionData} />
      ) : (
        <>
          <div className="dashboard-v2__stats site-dashboard-v2__stats">
            <article className="dashboard-v2__client-card">
              {loading ? (
                <Skeleton width={56} height={56} circle />
              ) : groupInfo?.avatar ? (
                <img src={groupInfo.avatar} alt="" className="dashboard-v2__client-logo" referrerPolicy="no-referrer" />
              ) : (
                <div className="dashboard-v2__client-initials">{groupInfo?.name ? getInitials(groupInfo.name) : ""}</div>
              )}
              <div>
                <strong>{loading ? <Skeleton width={120} /> : groupInfo?.name}</strong>
                <span>Client / Site</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
                <AssignmentOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.inspectionCount || 0}</strong>
                <span>Inspection Count</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
                <GradeOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.score || 0}</strong>
                <span>Score</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
                <ReportProblemOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.findingCount || 0}</strong>
                <span>Finding Count</span>
              </div>
            </article>
          </div>

          <article className="dashboard-v2__chart-card site-dashboard-v2__region-chart">
            <h3>Score By Region</h3>
            {loading ? <Skeleton height={320} /> : <BarChart list={regionResponseStats} />}
          </article>

          <NegativeResponsesV2 questions={questions} loading={loading} isSite={true} />
          <GroupWiseReportV2 list={list} loading={loading} isSite={true} />
        </>
      )}
    </div>
  );
};

export default SiteDashboardV2;
